import type React from 'react'
import {useState, useEffect, useCallback, useMemo, useRef} from 'react'
import {AppHeaderV2Wrapper} from '@/components/GDS/organisms/AppHeaderV2'
import SidebarWrapper from '@/components/GDS/organisms/Sidebar/SidebarWrapper'
import {useDispatch, useSelector} from 'react-redux'
import type {RootState, AppDispatch} from '@/store'
import {useTheme, ListV2RightAccessory} from 'pepsico-ds'
import {setTheme, updateThemeAsync} from '@/store/slices/theme'
import {useNavigate, useLocation} from 'react-router-dom'
import {getCurrentUserEmail, getUserProfileFromOkta} from '@/utils/userHelper'
import {useUserPermissions} from '@/hooks/useUserPermissions'
import {useAccess} from '@/hooks/useAccess'
import {useFeatureFlag} from '@/contexts/GrowthBookContext'
import {trackPageView, getSectionName} from '@/components/FullStory'
import type {LayoutProps, SidebarItem} from '@/layout/helpers/types'
import {PROFILE_NAME_MAX, PROFILE_EMAIL_MAX, LIGHT_THEME_LABEL, DARK_THEME_LABEL} from '@/layout/helpers/constants'
import {useActiveMenu, getSidebarItems} from '@/layout/helpers/layoutHelpers'
import {ASK_DEVLENS_AVAILABILITY_FLAG, PROFILE_MENU_ITEMS} from '@/routes/routeConfig'
import {DEVX_HOME_URL, DEVX_INFRA_URL, DEVX_DAILY_URL, DEVX_AI_URL, DEVX_LAUNCHPAD_URL} from '@/config/envConfig'
import GlobalToastContainer from '@/components/common/GlobalToastContainer'
import PageDocsButton from '@/components/common/PageDocsButton'
import {DataDefinitionDrawerProvider} from '@/components/common/DataDefinitionDrawer'
import {selectAccessSummary} from '@/store/slices/accessSummarySlice'

const Layout: React.FC<LayoutProps> = ({children}) => {
  // Set up state and Redux hooks
  const dispatch = useDispatch<AppDispatch>()
  const mode = useSelector((state: RootState) => state.theme.mode)
  const isDark = mode === 'dark'
  const accessSummary = useSelector(selectAccessSummary)
  const getAppIconPath = (appId: 'devx-home' | 'devx-infra' | 'devx-daily' | 'devx-ai' | 'devx-launch') =>
    `/images/${appId}-${isDark ? 'dark' : 'light'}.svg`

  const {setTheme: setPepsicoTheme} = useTheme()

  // UI state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true)
  const [allAppsOpen, setAllAppsOpen] = useState(false)
  const [activeMenuId, setActiveMenuId] = useState('dashboard')
  const sidebarRef = useRef<HTMLDivElement>(null)

  // Observe the sidebar for the "show-apps" class to detect All Apps panel
  useEffect(() => {
    const container = sidebarRef.current
    if (!container) return
    const observer = new MutationObserver(() => {
      const hasShowApps = container.querySelector('.sidebar-content.show-apps') !== null
      setAllAppsOpen(hasShowApps)
    })
    observer.observe(container, {attributes: true, subtree: true, attributeFilter: ['class']})
    return () => observer.disconnect()
  }, [])

  const applyTheme = (theme: 'light' | 'dark') => {
    // Update Redux immediately for instant UI feedback
    dispatch(setTheme(theme))
    // Then sync with API in background (fire and forget)
    dispatch(updateThemeAsync(theme))
  }

  // Always call the hook at the top level
  const navigate = useNavigate()
  const location = useLocation()
  const isAskDevLensV2Available = useFeatureFlag('ask-devlens-v2-available') === true

  const handleLogout = () => {
    // Use globalThis.location.href instead of navigate to ensure URL changes in browser
    globalThis.location.href = '/logout'
  }

  const handleProfileMenuNavigation = useCallback(
    (menuId: string) => {
      const menuItem = PROFILE_MENU_ITEMS.find(item => item.id === menuId)
      if (!menuItem) return

      const path = menuItem.path

      if (menuItem.openInNewTab) {
        globalThis.open(path, '_blank', 'noopener,noreferrer')
        return
      }

      if (location.pathname !== path) {
        navigate(path)
      }
    },
    [isAskDevLensV2Available, location.pathname, navigate],
  )

  // Sync Pepsico DS theme with Redux state (only runs when mode changes)
  useEffect(() => {
    const pepsicoTheme = mode === 'dark' ? DARK_THEME_LABEL : LIGHT_THEME_LABEL
    setPepsicoTheme(pepsicoTheme)
    document.documentElement.classList.toggle('dark', mode === 'dark')
  }, [mode, setPepsicoTheme])

  useActiveMenu(location.pathname, setActiveMenuId)

  // Set document title for each page (FullStory uses this for page names)
  useEffect(() => {
    const sectionName = getSectionName(location.pathname)
    // Set title with DevLens prefix - FullStory will use this as the page name
    document.title = `DevLens - ${sectionName}`
  }, [location.pathname])

  // Track page views in FullStory when location changes
  useEffect(() => {
    try {
      const sectionName = getSectionName(location.pathname)
      trackPageView(location.pathname, sectionName)
    } catch (error) {
      // Silently fail - don't break the app
      console.error('[Layout] FullStory tracking error:', error)
    }
  }, [location.pathname])

  const userProfile = getUserProfileFromOkta()
  const {isAdmin, userInfo} = useUserPermissions()
  const {hasAccess, acl, userRoles} = useAccess()
  const askDevLensAvailable = useFeatureFlag(ASK_DEVLENS_AVAILABILITY_FLAG) === true

  // Check if authentication is ready (user info is available)
  const isAuthReady = !!userInfo

  const truncateText = useCallback((text: string | undefined | null, maxLength: number) => {
    if (!text) return ''
    return text.length <= maxLength ? text : `${text.slice(0, maxLength - 1)}…`
  }, [])

  const truncatedName = truncateText(userProfile?.name ?? getCurrentUserEmail() ?? '', PROFILE_NAME_MAX)
  const truncatedEmail = truncateText(userProfile?.email ?? getCurrentUserEmail() ?? '', PROFILE_EMAIL_MAX)

  // Compute profile menu options based on ACL slugs (driven by centralized PROFILE_MENU_ITEMS)

  const profileMenuOptions = useMemo(() => {
    return PROFILE_MENU_ITEMS.filter(item => {
      if (item.id === 'manage-tag' && !accessSummary?.canManageAccess) {
        return false
      }

      if (item.id === 'ask-devlens' && (!askDevLensAvailable || !isAdmin)) {
        return false
      }

      return hasAccess(item.slug)
    })
  }, [hasAccess, accessSummary?.canManageAccess, askDevLensAvailable, isAdmin])

  // Sidebar nav items - all navigation entries filtered by user access
  const sidebarItems: SidebarItem[] = useMemo(() => {
    return getSidebarItems(userRoles, acl)
  }, [userRoles, acl])

  // DevX app links shown in sidebar "All Apps" panel
  const devxApps = useMemo(() => {
    const openInNew = (url: string) => () => globalThis.open(url, '_blank', 'noopener,noreferrer')
    const openInNewIcon = (url: string) => (
      <ListV2RightAccessory
        iconButtonProps={{icon: 'open_in_new', onClick: openInNew(url), size: 'medium', type: 'naked'}}
      />
    )
    return [
      {
        id: 'devx-home',
        contentGroup: {titleProps: {text: 'DevX - Home'}},
        imageProps: {source: getAppIconPath('devx-home'), width: '40px', height: '40px', objectFit: 'contain' as const},
        draggable: false,
        showNavigationIcon: false,
        onClick: openInNew(DEVX_HOME_URL),
        rightAccessory: openInNewIcon(DEVX_HOME_URL),
      },
      {
        id: 'devx-infra',
        contentGroup: {titleProps: {text: 'DevX - Infra'}},
        imageProps: {
          source: getAppIconPath('devx-infra'),
          width: '40px',
          height: '40px',
          objectFit: 'contain' as const,
        },
        draggable: false,
        showNavigationIcon: false,
        onClick: openInNew(DEVX_INFRA_URL),
        rightAccessory: openInNewIcon(DEVX_INFRA_URL),
      },
      {
        id: 'devx-daily',
        contentGroup: {titleProps: {text: 'DevX - Daily'}},
        imageProps: {
          source: getAppIconPath('devx-daily'),
          width: '40px',
          height: '40px',
          objectFit: 'contain' as const,
        },
        draggable: false,
        showNavigationIcon: false,
        onClick: openInNew(DEVX_DAILY_URL),
        rightAccessory: openInNewIcon(DEVX_DAILY_URL),
      },
      {
        id: 'devx-ai',
        contentGroup: {titleProps: {text: 'DevX - AI'}},
        imageProps: {source: getAppIconPath('devx-ai'), width: '40px', height: '40px', objectFit: 'contain' as const},
        draggable: false,
        showNavigationIcon: false,
        onClick: openInNew(DEVX_AI_URL),
        rightAccessory: openInNewIcon(DEVX_AI_URL),
      },
      {
        id: 'devx-launchpad',
        contentGroup: {titleProps: {text: 'DevX - Launchpad'}},
        imageProps: {
          source: getAppIconPath('devx-launch'),
          width: '40px',
          height: '40px',
          objectFit: 'contain' as const,
        },
        draggable: false,
        showNavigationIcon: false,
        onClick: openInNew(DEVX_LAUNCHPAD_URL),
        rightAccessory: openInNewIcon(DEVX_LAUNCHPAD_URL),
      },
    ]
  }, [isDark])

  const handleSidebarItemSelect = useCallback(
    (id: string) => {
      const selectedItem = sidebarItems.find(item => item.id === id)
      if (selectedItem?.url) {
        navigate(selectedItem.url)
      } else {
        navigate(`/${id}`)
      }
      setActiveMenuId(id)
      requestAnimationFrame(() => {
        setSidebarCollapsed(true)
      })
    },
    [navigate, sidebarItems],
  )

  const handleToggleCollapse = () => {
    setSidebarCollapsed(prev => !prev)
  }

  // If authentication is not ready yet, don't render anything
  if (!isAuthReady) {
    return null
  }

  return (
    <div className="bg-theme-default flex min-h-screen flex-col">
      {/* Fixed header with high z-index */}
      <div className="fixed top-0 right-0 left-0 z-[200]">
        <AppHeaderV2Wrapper
          appLogoProps={{
            appName: 'DevLens',
            showAppName: true,
            customLogo: {
              src: '/images/devlens-logo-header.svg',
              alt: 'DevLens Logo',
            },
            showCustomLogo: true,
          }}
          logoProps={{
            logoType: 'pepsicoFullLogo',
            colorStyle: 'white',
            onClick: () => navigate('/'),
            style: {cursor: 'pointer'},
          }}
          profileMenuProps={{
            title: 'Profile',
            profileDetails: {
              name: truncatedName,
              email: truncatedEmail,
              onClick: () => {},
              ...(isAdmin
                ? {
                    contentGroup: {
                      titleProps: {
                        text: truncatedName,
                        tag: {
                          color: 'orange',
                          text: 'Admin',
                          type: 'faded',
                        },
                      },
                    },
                  }
                : {}),
            },
            switcher: {
              onUpdate: option => {
                if (option === LIGHT_THEME_LABEL) {
                  applyTheme('light')
                } else if (option === DARK_THEME_LABEL) {
                  applyTheme('dark')
                }
              },
              defaultSelection: isDark ? DARK_THEME_LABEL : LIGHT_THEME_LABEL,
            },
            options: profileMenuOptions,
            onClickOption: id => {
              if (id === 'logout') {
                handleLogout()
              } else {
                handleProfileMenuNavigation(id)
              }
              // Close profile menu after option selection
              globalThis.dispatchEvent(new KeyboardEvent('keydown', {key: 'Escape'}))
            },
            showThemeSwitcher: true,
            onClickLogout: handleLogout,
          }}
        />
      </div>

      <div className="relative mt-[64px] flex h-[calc(100vh-64px)] overflow-hidden">
        {/* Sidebar - part of flex flow so expanding pushes content */}
        <div
          ref={sidebarRef}
          className="relative z-20 shrink-0 transition-all duration-300 [&_*]:caret-transparent [&_*]:outline-none"
          style={{width: allAppsOpen || !sidebarCollapsed ? 300 : 72}}
        >
          <div className="h-full">
            <SidebarWrapper
              items={sidebarItems}
              activeItem={sidebarItems.find(item => item.id === activeMenuId)}
              navLinkAction={(item: {id: string}) => handleSidebarItemSelect(item.id)}
              showBottomAction={true}
              showCollapsed={sidebarCollapsed}
              onToggle={handleToggleCollapse}
              apps={devxApps}
            />
          </div>
        </div>
        {/* Main content - naturally squeezes when sidebar expands */}
        <main className="relative min-w-0 flex-1 overflow-auto p-6 pt-0">
          <DataDefinitionDrawerProvider>{children}</DataDefinitionDrawerProvider>
        </main>
      </div>

      {/* Global Toast Container */}
      <GlobalToastContainer />

      {/* Floating docs icon, enabled per page via the GrowthBook route feature */}
      <PageDocsButton />
    </div>
  )
}

export default Layout
